import React, { type FC, useState } from "react";
import { useIntl } from "react-intl";
import {
  Divider,
  SpacingVertical,
  Title,
  Text,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  FlexItem,
  Flex,
  FormElement,
  Checkbox,
  HelpIcon,
  Select,
} from "mailup-web-components";
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";
import { importFileWizardState } from "Pages/ImportWizard/recoil/state/state";
import { importFilesPreviewCallback } from "Pages/ImportWizard/recoil/callbacks";
import { notifyParent } from "mailup-react-helpers";
import { openHelp } from "Helpers/openHelp";
import { gainsightTrack } from "mailup-react-helpers";

const RedingSettings: FC = () => {
  const intl = useIntl();

  const { Separator, Delimitator, IgnoreDoubleQuotes } = useRecoilValue(
    importFileWizardState,
  );
  const setImportFileWizardState = useSetRecoilState(importFileWizardState);
  const importFilesPreview = useRecoilCallback(importFilesPreviewCallback, []);

  const openGuide = () => {
    notifyParent("openHelp", [openHelp("importFileGuide")]);
  };

  const onChangeSeparator = (value: string) => {
    setImportFileWizardState((prev) => ({
      ...prev,
      Separator: value,
    }));
    importFilesPreview();
  };

  const onChangeDelimitator = (value: string) => {
    setImportFileWizardState((prev) => ({
      ...prev,
      Delimitator: value,
    }));
    importFilesPreview();
  };

  const onChangeIgnoreDoubleQuotes = () => {
    gainsightTrack("mup_importWizard_clickIgnoreCurlyQuotes", {});

    setImportFileWizardState((prev) => ({
      ...prev,
      IgnoreDoubleQuotes: !prev.IgnoreDoubleQuotes,
    }));
    importFilesPreview();
  };

  return (
    <>
      <SpacingVertical variant="8" />
      <Title variant="M">
        {intl.formatMessage({ id: "Load.FileImport.RedingSettingsTitle" })}
      </Title>
      <SpacingVertical variant="8" />
      <Text>
        {intl.formatMessage(
          { id: "Load.FileImport.RedingSettingsDescr" },
          {
            link: (chunks) => (
              <Link
                variant={LINK_VARIANT_HIGHLIGHT}
                onClick={() => openGuide()}
              >
                {chunks}
              </Link>
            ),
          },
        )}
      </Text>
      <SpacingVertical />
      <Flex gap="24px" alignItems="center">
        <FlexItem flex="1">
          <FormElement
            label={intl.formatMessage({
              id: "Load.FileImport.SeparatorLabel",
            })}
            hint={intl.formatMessage({
              id: "Load.FileImport.SeparatorHinit",
            })}
          >
            <Select
              searchable={false}
              onChange={onChangeSeparator}
              value={Separator}
              options={[
                { label: ",", value: "Comma" },
                { label: ";", value: "Semicolon" },
                { label: "tab", value: "Tabulator" },
                { label: "|", value: "Pipe" },
              ]}
            />
          </FormElement>
        </FlexItem>
        <FlexItem flex="1">
          <FormElement
            label={intl.formatMessage({
              id: "Load.FileImport.DelimiterLabel",
            })}
            hint={intl.formatMessage({
              id: "Load.FileImport.DelimiterHinit",
            })}
          >
            <Select
              searchable={false}
              onChange={onChangeDelimitator}
              value={Delimitator}
              options={[
                { label: '"', value: "DoubleQuotes" },
                {
                  label: intl.formatMessage({
                    id: "Load.FileImport.NoDelimiter",
                  }),
                  value: "None",
                },
                { label: "'", value: "SingleQuotes" },
              ]}
            />
          </FormElement>
        </FlexItem>
        <FlexItem flex="1">
          <Flex alignItems="center">
            <Checkbox
              label={intl.formatMessage({
                id: "Load.FileImport.IgnoreDoubleQuotes",
              })}
              checked={IgnoreDoubleQuotes}
              onChange={onChangeIgnoreDoubleQuotes}
            />
            <HelpIcon
              data-tip={intl.formatMessage({
                id: "Load.FileImport.IgnoreDoubleQuotesTooltip",
              })}
            />
          </Flex>
        </FlexItem>
      </Flex>
    </>
  );
};
export default RedingSettings;
